import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Typography, IconButton, Snackbar, Alert, Button, LinearProgress, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import VideoRecorderModal from "./VideoRecorderModal";
import PhotoCaptureModal from "./PhotoCaptureModal";
import WizardMediaCarousel from "./components/WizardMediaCarousel";
import ForwardedFeaturedNotice from "./components/ForwardedFeaturedNotice";
import { isFeaturedCapsuleType } from "../../../../utils/timeCapsuleForward";
import { LightboxModal } from "../../../common/LightboxModal";
import useAppConfigFlags from "../../../../hooks/useAppConfigFlags";
import {
  buildSessionPreviewDataUrl,
  buildVideoSessionPreviewDataUrl,
} from "../../../../utils/timeCapsuleMedia";
import { alpha } from "@mui/material/styles";

const MAX_VIDEO_SECONDS = 10;
const MAX_VIDEO_VALIDATION_SECONDS = 11;
const PLACE_PREFILL_MAX_IMAGES = 5;
const VIDEO_FILE_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "m4v",
  "webm",
  "ogg",
  "ogv",
  "3gp",
  "3gpp",
]);

function extFromMime(mime = "") {
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("quicktime")) return "mov";
  return "webm";
}

function getFileExtension(name = "") {
  const cleanName = String(name || "").split("?")[0].split("#")[0];
  const parts = cleanName.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function looksLikeVideoFile(file) {
  if (!file) return false;
  const rawType = String(file.type || "").toLowerCase();
  if (rawType.startsWith("video/")) return true;
  if (rawType.startsWith("image/")) return false;
  return VIDEO_FILE_EXTENSIONS.has(getFileExtension(file.name));
}

function normalizePickedFileType(file, forceVideo = false) {
  const rawType = String(file?.type || "").trim().toLowerCase();
  if (forceVideo) {
    if (rawType.startsWith("video/")) return rawType;
    return "video/mp4";
  }
  if (rawType) return rawType;
  return looksLikeVideoFile(file) ? "video/mp4" : "image/jpeg";
}

function looksLikeVideoMedia(item) {
  if (!item) return false;
  const rawType = String(item.type || "").toLowerCase();
  if (rawType.startsWith("video/")) return true;
  if (rawType.startsWith("image/")) return false;
  if (looksLikeVideoFile(item.file)) return true;
  return VIDEO_FILE_EXTENSIONS.has(getFileExtension(item.name || item.url || ""));
}

async function getVideoDurationSeconds(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);

    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
      video.removeAttribute("src");
      video.load();
    };

    video.onerror = () => {
      cleanup();
      reject(new Error("Failed to read video metadata"));
    };

    video.onloadedmetadata = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        cleanup();
        resolve(video.duration);
        return;
      }
      video.ontimeupdate = () => {
        video.ontimeupdate = null;
        if (Number.isFinite(video.duration) && video.duration > 0) {
          cleanup();
          resolve(video.duration);
        } else {
          cleanup();
          reject(new Error("Video duration unavailable"));
        }
      };
      try {
        video.currentTime = 1e101;
      } catch {
        cleanup();
        reject(new Error("Video duration unavailable"));
      }
    };

    video.src = url;
  });
}

async function validatePickedFile(file) {
  if (!file) return { ok: false, reason: "No file selected." };

  const mimeType = String(file.type || "").toLowerCase();
  const shouldProbeAsVideo = mimeType.startsWith("video/") || !mimeType || looksLikeVideoFile(file);

  if (!shouldProbeAsVideo) return { ok: true, isVideo: false };

  try {
    const duration = await getVideoDurationSeconds(file);
    const isVideo = Number.isFinite(duration) && duration > 0;
    if (!isVideo) {
      return { ok: true, isVideo: false };
    }
    if (duration > MAX_VIDEO_VALIDATION_SECONDS) {
      return {
        ok: false,
        reason: `That video is too long. Please choose ${MAX_VIDEO_SECONDS} seconds or less.`,
        isVideo: true,
      };
    }
    return { ok: true, isVideo: true };
  } catch {
    if (!mimeType.startsWith("video/") && !looksLikeVideoFile(file)) {
      return { ok: true, isVideo: false };
    }
    return {
      ok: false,
      reason: `We couldn’t read that video’s length. Please choose ${MAX_VIDEO_SECONDS}s or less.`,
      isVideo: true,
    };
  }
}

function mediaLimits(enabled) {
  if (!enabled) return { maxTotal: 1, maxVideos: 1 };
  return { maxTotal: 12, maxVideos: 2 };
}

function countKinds(items) {
  return (items || []).reduce(
    (acc, item) => {
      if (looksLikeVideoMedia(item)) acc.videos += 1;
      else acc.images += 1;
      return acc;
    },
    { images: 0, videos: 0 }
  );
}

function getLimitError(items, item, limits) {
  const { videos } = countKinds(items);
  const isVideo = looksLikeVideoMedia(item);

  if (items.length >= limits.maxTotal) {
    return `You can add up to ${limits.maxTotal} files total (max ${limits.maxVideos} videos).`;
  }
  if (isVideo && videos >= limits.maxVideos) {
    return `You can attach up to ${limits.maxVideos} videos (${MAX_VIDEO_SECONDS}s max each).`;
  }
  return "";
}

function withClientId(item) {
  return {
    ...item,
    clientId:
      item?.clientId ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  };
}

function getPickedFileDiagnostics(file) {
  if (!(file instanceof File)) return { hasFile: false };
  return {
    hasFile: true,
    name: file.name || "",
    type: file.type || "",
    size: file.size,
    lastModified: file.lastModified || 0,
  };
}

function extensionFromMimeType(mimeType = "") {
  const normalized = String(mimeType || "").toLowerCase();
  if (normalized.includes("webp")) return "webp";
  if (normalized.includes("png")) return "png";
  if (normalized.includes("gif")) return "gif";
  if (normalized.includes("jpeg") || normalized.includes("jpg")) return "jpg";
  return "jpg";
}

async function buildPlacePrefillItem(placePhoto, index) {
  try {
    const placeUrl = placePhoto?.getUrl ? placePhoto.getUrl({ maxWidth: 1600 }) : "";
    if (!placeUrl) return null;

    try {
      const response = await fetch(placeUrl);
      if (!response.ok) throw new Error(`Failed to fetch place photo (${response.status})`);
      const blob = await response.blob();
      const mimeType = String(blob?.type || "image/jpeg").toLowerCase();
      const extension = extensionFromMimeType(mimeType);
      const file = new File([blob], `place_${Date.now()}_${index}.${extension}`, {
        type: mimeType || "image/jpeg",
      });
      const objectUrl = URL.createObjectURL(file);
      return withClientId({
        file,
        url: objectUrl,
        type: mimeType || "image/jpeg",
        source: "place",
      });
    } catch {
      // Fallback to direct URL if browser/network policy blocks downloading photo bytes.
      return withClientId({
        file: null,
        url: placeUrl,
        type: "image/jpeg",
        source: "place",
      });
    }
  } catch {
    return null;
  }
}

export default function Step2MediaFrictionless({
  wizardData,
  setWizardData,
  onBack,
  onNext,
  onSaveAndExit,
  primaryActionLabel = "Bury",
  isSavingDraft = false,
  isUploadingBeforeShare = false,
  uploadProgress = 0,
  uploadError = "",
  onClearUploadError,
  invalidMediaByClientId = {},
  onRemoveUploadedMedia,
}) {
  const { privateCapsuleMultiMediaEnabled } = useAppConfigFlags();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [isProcessingSelection, setIsProcessingSelection] = useState(false);
  const placePhotoPrefillTriedRef = useRef(false);

  const isIOS = useMemo(
    () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
    []
  );
  const isIOSSafari = useMemo(() => {
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  }, []);
  const canUseMediaRecorder = useMemo(() => typeof MediaRecorder !== "undefined", []);

  const actionBarPaddingBottom = useMemo(
    () =>
      isIOSSafari
        ? "calc(env(safe-area-inset-bottom) + 28px)"
        : "calc(env(safe-area-inset-bottom) + 12px)",
    [isIOSSafari]
  );
  const galleryProcessingLabel = isIOS
    ? "iOS is preparing selected photos..."
    : "Adding media from gallery...";
  const galleryProcessingHint = isIOS
    ? "This can take a few seconds for large or iCloud photos."
    : "";

  const limits = mediaLimits(privateCapsuleMultiMediaEnabled);
  const mediaFiles = wizardData.mediaFiles || [];
  const mediaCount = mediaFiles.length;
  const counts = countKinds(mediaFiles);
  const isBusy = isUploadingBeforeShare || isProcessingSelection;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (placePhotoPrefillTriedRef.current) return;
    const placeId = String(wizardData?.placeId || "").trim();
    if (!placeId) return;
    if (mediaCount > 0) {
      placePhotoPrefillTriedRef.current = true;
      return;
    }

    const g = window.google?.maps;
    if (!g?.places?.PlacesService) return;

    placePhotoPrefillTriedRef.current = true;
    const service = new g.places.PlacesService(document.createElement("div"));
    service.getDetails({ placeId, fields: ["photos", "name"] }, async (place, status) => {
      if (status !== "OK" || !Array.isArray(place?.photos) || place.photos.length === 0) return;
      const maxPrefill = Math.min(PLACE_PREFILL_MAX_IMAGES, place.photos.length);
      const placePhotos = place.photos.slice(0, maxPrefill);
      const resolvedItems = await Promise.all(
        placePhotos.map((photo, index) => buildPlacePrefillItem(photo, index))
      );
      const items = resolvedItems.filter(Boolean);

      if (!items.length) return;
      setWizardData((prev) => {
        if ((prev.mediaFiles || []).length > 0) return prev;
        return { ...prev, mediaFiles: items };
      });
    });
  }, [mediaCount, setWizardData, wizardData.placeId]);

  const showSnack = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const addMediaItem = (item) => {
    const current = wizardData.mediaFiles || [];
    const normalizedItem = withClientId(item);
    const limitError = getLimitError(current, normalizedItem, limits);
    if (limitError) {
      showSnack(
        privateCapsuleMultiMediaEnabled
          ? limitError
          : "You can add 1 media file in this mode."
      );
      return false;
    }

    setWizardData((prev) => ({ ...prev, mediaFiles: [normalizedItem, ...(prev.mediaFiles || [])] }));
    return true;
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    try {
      e.target.value = "";
    } catch {
      // ignore
    }

    if (!files.length) return;

    setIsProcessingSelection(true);
    const current = wizardData.mediaFiles || [];
    const toAdd = [];
    let skipped = 0;
    let firstError = "";
    try {
      for (let fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
        const file = files[fileIndex];
        const nextItems = [...current, ...toAdd];
        if (nextItems.length >= limits.maxTotal) {
          skipped += files.length - fileIndex;
          if (!firstError) {
            firstError = `You can add up to ${limits.maxTotal} files total (max ${limits.maxVideos} videos).`;
          }
          break;
        }

        const validation = await validatePickedFile(file);
        if (!validation.ok) {
          skipped += 1;
          if (!firstError) firstError = validation.reason || "Unsupported media.";
          continue;
        }

        const candidateIsVideo = Boolean(validation.isVideo);
        const candidate = withClientId({
          file,
          type: normalizePickedFileType(file, candidateIsVideo),
        });

        const limitError = getLimitError(nextItems, candidate, limits);
        if (limitError) {
          skipped += 1;
          if (!firstError) firstError = limitError;
          continue;
        }

        candidate.url = URL.createObjectURL(file);

        try {
          if (!candidateIsVideo && String(candidate.type || "").toLowerCase().startsWith("image/")) {
            const sessionPreviewUrl = await buildSessionPreviewDataUrl(file);
            if (sessionPreviewUrl) {
              candidate.sessionPreviewUrl = sessionPreviewUrl;
            }
          } else if (candidateIsVideo || looksLikeVideoFile(file)) {
            const sessionPreviewUrl = await buildVideoSessionPreviewDataUrl(file);
            if (sessionPreviewUrl) {
              candidate.sessionPreviewUrl = sessionPreviewUrl;
            }
          }
        } catch (err) {
          console.warn("Selected media preview could not be generated.", getPickedFileDiagnostics(file), err);
          // Keep the selected media even if best-effort preview generation fails.
        }

        toAdd.push(candidate);
      }

      if (toAdd.length > 0) {
        setWizardData((prev) => ({ ...prev, mediaFiles: [...toAdd, ...(prev.mediaFiles || [])] }));
      }

      if (skipped > 0) {
        if (firstError) {
          showSnack(firstError);
        } else {
          showSnack(`${skipped} file${skipped === 1 ? "" : "s"} could not be added.`);
        }
      }
    } finally {
      setIsProcessingSelection(false);
    }
  };

  const handlePhotoCapture = async (imageSrc) => {
    try {
      const resp = await fetch(imageSrc);
      const blob = await resp.blob();
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      addMediaItem({ file, url: imageSrc, type: "image/jpeg", sessionPreviewUrl: imageSrc });
    } catch (err) {
      console.error("Photo capture error:", err);
      showSnack("Failed to process captured photo.");
    } finally {
      setShowPhotoCapture(false);
    }
  };

  const handleVideoRecorded = async ({ blob, mimeType }) => {
    try {
      const safeMime = mimeType || blob?.type || "video/webm";
      const ext = extFromMime(safeMime);
      const newUrl = URL.createObjectURL(blob);
      const file = blob instanceof Blob ? new File([blob], `video_${Date.now()}.${ext}`, { type: safeMime }) : null;
      addMediaItem({
        file,
        url: newUrl,
        type: safeMime,
      });

      if (file) {
        buildVideoSessionPreviewDataUrl(file)
          .then((sessionPreviewUrl) => {
            if (!sessionPreviewUrl) return;
            setWizardData((prev) => ({
              ...prev,
              mediaFiles: (prev.mediaFiles || []).map((item) =>
                item?.url === newUrl
                  ? { ...item, sessionPreviewUrl }
                  : item
              ),
            }));
          })
          .catch(() => {
            // ignore thumbnail extraction failures on some browsers
          });
      }
    } catch (err) {
      console.error("Video record error:", err);
      showSnack("Failed to process recorded video.");
    } finally {
      setShowVideoRecorder(false);
    }
  };

  const handleRemoveMedia = async (index) => {
    const removed = mediaFiles[index];
    setWizardData((prev) => {
      const arr = [...(prev.mediaFiles || [])];
      const removedFromState = arr[index];
      if (removedFromState?.url?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(removedFromState.url);
        } catch {
          // ignore
        }
      }
      arr.splice(index, 1);
      return { ...prev, mediaFiles: arr };
    });
    if (typeof onRemoveUploadedMedia === "function" && removed?.storagePath) {
      await onRemoveUploadedMedia(removed);
    }
  };

  const openLightbox = (item, index = 0) => {
    if (!item?.url) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const mediaActionButtonSx = (theme) => {
    const isDarkMode = theme.palette.mode === "dark";

    return {
      flex: 1,
      minHeight: 86,
      borderRadius: 1,
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      minWidth: 0,
      fontWeight: 700,
      textTransform: "none",
      border: "2px solid",
      borderColor: isDarkMode
        ? alpha(theme.colors.blue, 0.4)
        : theme.colors.blue,
      backgroundColor: isDarkMode
        ? alpha(theme.palette.common.white, 0.05)
        : theme.palette.background.paper,
      color: isDarkMode
        ? alpha(theme.palette.common.white, 0.9)
        : theme.colors.blue,
      px: 1,
      py: 1.15,
      fontSize: { xs: "0.76rem", sm: "0.85rem" },
      lineHeight: 1.1,
      boxShadow: isDarkMode
        ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.04)}`
        : `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
      "& .MuiButton-startIcon": {
        color: isDarkMode ? alpha(theme.colors.blue, 0.92) : theme.colors.blue,
        margin: 0,
        marginBottom: 0.55,
      },
      "& .MuiSvgIcon-root": {
        fontSize: 28,
      },
      "&:hover": {
        borderColor: isDarkMode
          ? alpha(theme.colors.blue, 0.62)
          : theme.colors.blue,
        backgroundColor: isDarkMode
          ? alpha(theme.colors.blue, 0.12)
          : alpha(theme.colors.blue, 0.05),
        transform: "translateY(-1px)",
        boxShadow: isDarkMode
          ? `0 8px 18px ${alpha(theme.palette.common.black, 0.28)}`
          : `0 6px 14px ${alpha(theme.colors.blue, 0.12)}`,
      },
      "&.Mui-disabled": {
        borderColor: isDarkMode
          ? alpha(theme.palette.common.white, 0.1)
          : alpha(theme.palette.text.primary, 0.12),
        backgroundColor: isDarkMode
          ? alpha(theme.palette.common.white, 0.06)
          : theme.palette.action.disabledBackground,
        color: isDarkMode
          ? alpha(theme.palette.common.white, 0.32)
          : theme.palette.action.disabled,
        boxShadow: "none",
      },
      "&.Mui-disabled .MuiButton-startIcon": {
        color: isDarkMode
          ? alpha(theme.palette.common.white, 0.26)
          : theme.palette.action.disabled,
      },
      transition: "transform 140ms ease, background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
    };
  };

  const uploadButtonSx = (theme) => {
    const isDarkMode = theme.palette.mode === "dark";

    return {
      fontWeight: 700,
      border: 0,
      px: 3,
      py: 1.25,
      flex: 1,
      width: "100%",
      borderRadius: "999px",
      backgroundColor: isDarkMode
        ? alpha(theme.palette.common.white, 0.12)
        : theme.palette.action.disabledBackground,
      color: isDarkMode
        ? alpha(theme.palette.common.white, 0.5)
        : theme.palette.action.disabled,
      boxShadow: "none",
    };
  };

  useEffect(() => {
    if (!uploadError) return;
    setSnackbarMessage(uploadError);
    setSnackbarOpen(true);
  }, [uploadError]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "contain",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          px: 4,
          pt: 2,
          pb: 2,
        }}
      >
        {isFeaturedCapsuleType(wizardData?.forwardedFromType) && (
          <ForwardedFeaturedNotice
            title={wizardData?.forwardSourceTitle}
            avatarUrl={wizardData?.forwardSourceAvatarUrl}
          />
        )}
        <Typography variant="caption" sx={{ display: "block", mb: 1, opacity: 0.8 }}>
          {privateCapsuleMultiMediaEnabled
            ? `Add up to ${limits.maxTotal} files. Up to ${limits.maxVideos} can be videos (${MAX_VIDEO_SECONDS}s max each).`
            : "Single media mode: 1 file"}
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mb: 1.5, opacity: 0.75 }}>
          {privateCapsuleMultiMediaEnabled
            ? `Selected ${mediaFiles.length}/${limits.maxTotal} files · ${counts.videos}/${limits.maxVideos} videos`
            : `Selected ${mediaFiles.length}/1 file`}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={() => setShowPhotoCapture(true)}
            startIcon={<PhotoCameraOutlinedIcon />}
            sx={mediaActionButtonSx}
          >
            Photo
          </Button>

          {isIOS && !canUseMediaRecorder ? (
            <Button
              variant="contained"
              component="label"
              disabled={isBusy}
              startIcon={<VideocamOutlinedIcon />}
              sx={mediaActionButtonSx}
            >
              Video
              <input
                hidden
                type="file"
                accept="video/*"
                capture="environment"
                onChange={handleMediaUpload}
              />
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={isBusy}
              onClick={() => setShowVideoRecorder(true)}
              startIcon={<VideocamOutlinedIcon />}
              sx={mediaActionButtonSx}
            >
              Video
            </Button>
          )}

          <Button
            variant="contained"
            component="label"
            disabled={isBusy}
            startIcon={<CollectionsOutlinedIcon />}
            sx={mediaActionButtonSx}
          >
            Gallery
            <input
              hidden
              type="file"
              accept="image/*,video/*"
              multiple={privateCapsuleMultiMediaEnabled}
              onChange={handleMediaUpload}
            />
          </Button>
        </Box>

        {mediaFiles.length > 0 && (
          <WizardMediaCarousel
            items={mediaFiles}
            mediaHeight={250}
            onOpenLightbox={openLightbox}
            invalidMediaByClientId={invalidMediaByClientId}
            onRemoveItem={(index) => {
              handleRemoveMedia(index);
            }}
            disableRemove={isBusy}
          />
        )}
      </Box>

      <PhotoCaptureModal open={showPhotoCapture} onClose={() => setShowPhotoCapture(false)} onCapture={handlePhotoCapture} />
      <VideoRecorderModal open={showVideoRecorder} onClose={() => setShowVideoRecorder(false)} onRecorded={handleVideoRecorded} maxSeconds={MAX_VIDEO_SECONDS} />
      <LightboxModal
        items={mediaFiles}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={closeLightbox}
      />

      <Box
        sx={{
          flex: "0 0 auto",
          mt: "auto",
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 2,
          pt: 1.5,
          pb: actionBarPaddingBottom,
        }}
      >
        {isUploadingBeforeShare && (
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="body2" sx={{ mb: 0.6, opacity: 0.85 }}>
              Uploading media: {uploadProgress}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={(theme) => ({
                height: 8,
                borderRadius: 999,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.colors.blue, 0.18)
                    : undefined,
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.colors.blue
                      : undefined,
                },
              })}
            />
          </Box>
        )}
        {isProcessingSelection && (
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="body2" sx={{ mb: 0.6, opacity: 0.85 }}>
              {galleryProcessingLabel}
            </Typography>
            {galleryProcessingHint && (
              <Typography variant="caption" sx={{ display: "block", mb: 0.8, opacity: 0.75 }}>
                {galleryProcessingHint}
              </Typography>
            )}
            <LinearProgress
              sx={(theme) => ({
                height: 8,
                borderRadius: 999,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.colors.blue, 0.18)
                    : undefined,
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.colors.blue
                      : undefined,
                },
              })}
            />
          </Box>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isBusy
              ? "1fr"
              : "48px minmax(0, 1fr) 122px",
            gap: 1.5,
            alignItems: "center",
            width: "100%",
          }}
        >
          {!isBusy && (
            <IconButton
              onClick={onBack}
              sx={(theme) => ({
                borderRadius: "999px",
                width: 48,
                height: 48,
                flex: "0 0 auto",
                border: "1px solid",
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.02)
                  : "transparent",
                color: theme.palette.text.primary,
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.primary, 0.06)
                    : alpha(theme.palette.text.primary, 0.02),
                },
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled,
                  borderColor: theme.palette.action.disabled,
                  backgroundColor: "transparent",
                },
              })}
              aria-label="Back"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          {!isBusy && (
            <Button
              variant="outlined"
              onClick={onSaveAndExit}
              disabled={isSavingDraft}
              startIcon={isSavingDraft ? <CircularProgress size={16} color="inherit" /> : null}
              sx={(theme) => ({
                fontWeight: 700,
                px: 3,
                py: 1.25,
                width: "100%",
                minWidth: 0,
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.mode === "dark"
                  ? alpha(theme.palette.text.primary, 0.02)
                  : "transparent",
                color: theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.primary, 0.06)
                    : alpha(theme.palette.text.primary, 0.02),
                },
                "&.Mui-disabled": {
                  color: theme.palette.text.disabled,
                  borderColor: theme.palette.action.disabledBackground,
                  backgroundColor: "transparent",
                },
              })}
            >
              {isSavingDraft ? "Saving..." : "Save & exit"}
            </Button>
          )}

          {isBusy ? (
            <Button
              variant="contained"
              onClick={onNext}
              disabled
              endIcon={isUploadingBeforeShare ? <CircularProgress size={16} color="inherit" /> : null}
              sx={uploadButtonSx}
            >
              {isUploadingBeforeShare ? `Uploading ${uploadProgress}%` : galleryProcessingLabel}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={onNext}
              endIcon={<Box component="img" src={IconNextArrow} alt="" sx={{ width: 16, height: 16, flexShrink: 0 }} />}
              sx={(theme) => ({
                fontWeight: 700,
                backgroundColor: theme.colors.blue,
                color: theme.colors.white,
                px: 2,
                py: 1.25,
                width: "100%",
                minWidth: 122,
                whiteSpace: "nowrap",
                boxShadow: "none",
                "& .MuiButton-endIcon": {
                  ml: 0.75,
                  mr: 0,
                  flexShrink: 0,
                },
                WebkitTapHighlightColor: "transparent",
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: `${theme.colors.blue} !important`,
                },
                "&:active": {
                  backgroundColor: `${theme.colors.blue} !important`,
                },
                "&.Mui-focusVisible": {
                  backgroundColor: `${theme.colors.blue} !important`,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              })}
              aria-label={primaryActionLabel}
            >
              {primaryActionLabel}
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          if (typeof onClearUploadError === "function") onClearUploadError();
        }}
      >
        <Alert
          onClose={() => {
            setSnackbarOpen(false);
            if (typeof onClearUploadError === "function") onClearUploadError();
          }}
          variant="filled"
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
